// src/components/redemittel/RedemittelCard.jsx
// Rebuilt from VocabularyCard.jsx blueprint, simple flip state, no refs/timers/debounce.
import React, { useState, useEffect } from 'react';
import { RotateCw, Volume2, Tag } from 'lucide-react';
import { CardState } from '../../services/srsService';
import ttsService from '../../services/ttsService';
import { useTranslation } from 'react-i18next';

const redemittelStyle = {
  gradient: 'from-purple-400 to-blue-600',
  indicator: 'bg-gradient-to-r from-purple-500 to-blue-600',
  textColor: 'text-purple-600',
  bgColor: 'bg-purple-50',
  borderColor: 'border-purple-300',
};

/**
 * Redemittel card, mirrors VocabularyCard architecture.
 * Parent supplies dragOffset / isDragging; flip is local state only.
 */
const RedemittelCard = ({
  card,
  srsData,
  dragOffset,
  isDragging,
  onFlipChange,
  isActive = true,
  zIndex = 1,
}) => {
  const { t } = useTranslation();
  const [isFlipped, setIsFlipped] = useState(false);
  const [revealedExample, setRevealedExample] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);

  useEffect(() => {
    setIsFlipped(false);
    setRevealedExample(false);
    if (onFlipChange) onFlipChange(false);
  }, [card.id]);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Keyboard shortcuts for pronunciation
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Skip if user is dragging
      if (isDragging) return;
      
      // Space: Play sound when card is flipped
      if (e.code === 'Space' && isFlipped && !isPlayingAudio) {
        e.preventDefault();
        playPronunciation(e);
      }
      
      // Ctrl/Cmd + V: Play sound anytime
      if ((e.ctrlKey || e.metaKey) && e.key === 'v' && !isPlayingAudio) {
        e.preventDefault();
        playPronunciation(e);
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isFlipped, isPlayingAudio, isDragging]);

  const getStateBadge = () => {
    if (!srsData) return null;
    switch (srsData.state) {
      case CardState.NEW:
        return { text: 'Neu', color: 'bg-blue-500' };
      case CardState.LEARNING:
        return { text: 'Lernen', color: 'bg-yellow-500' };
      case CardState.REVIEW:
        return { text: 'Wiederholen', color: 'bg-green-500' };
      case CardState.RELEARNING:
        return { text: 'Neu lernen', color: 'bg-orange-500' };
      default:
        return null;
    }
  };

  const stateBadge = getStateBadge();

  const handleFlip = () => {
    if (!isActive || isDragging) return;
    const nextState = !isFlipped;
    setIsFlipped(nextState);
    if (onFlipChange) onFlipChange(nextState);
  };

  const playPronunciation = async (e) => {
    e.stopPropagation();
    if (!card?.front || isPlayingAudio) return;
    
    setIsPlayingAudio(true);
    try {
      await ttsService.speak(card.front, { 
        voice: 'female_warm',     // Warm voice for conversational Redemittel phrases
        speed: 0.9,               // Slightly faster for natural flow
        cache: true,              // Cache to prevent redundant API calls
        priority: 'high'          // High priority for phrases
      });
    } catch (err) {
      console.warn('Pronunciation unavailable:', err?.message || err);
    } finally {
      setIsPlayingAudio(false);
    }
  };

  const toggleExample = (e) => {
    e.stopPropagation();
    setRevealedExample((prev) => !prev);
  };

  const rotation = dragOffset ? dragOffset.x * 0.1 : 0;
  const outerStyle = {
    transform: isDragging && dragOffset
      ? `translate(${dragOffset.x}px, ${dragOffset.y}px) rotate(${rotation}deg)`
      : 'none',
    transition: isDragging ? 'none' : 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
    zIndex,
  };

  const frontContent = (
    <>
      <div className={`w-full h-1.5 sm:h-2 ${redemittelStyle.indicator} flex-shrink-0`} />
      {stateBadge && (
        <div className="absolute top-2 right-2 z-10">
          <span className={`${stateBadge.color} text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium`}>
            {stateBadge.text}
          </span>
        </div>
      )}
      <div className="flex flex-col items-center justify-center flex-1 p-2.5 sm:p-8 gap-4 sm:gap-6">
        <div className={`w-full max-w-[120px] sm:max-w-[200px] aspect-square rounded-lg sm:rounded-xl flex items-center justify-center bg-gradient-to-br ${redemittelStyle.gradient} flex-shrink-0`}>
          <div className="text-xl sm:text-4xl md:text-5xl">{card.icon || '💬'}</div>
        </div>
        <div className="flex flex-col items-center text-center gap-2 sm:gap-4">
          <h2 className="text-xl sm:text-[clamp(1.5rem,4vw,2.5rem)] font-bold text-gray-900">
            {card.front}
          </h2>
          {card.context && (
            <span className="text-[10px] sm:text-xs text-gray-500 bg-gray-100 rounded-md px-2 py-0.5">
              {card.context}
            </span>
          )}
          <button
            onClick={playPronunciation}
            disabled={isPlayingAudio}
            className={`mx-auto mb-4 p-2 rounded-full transition-all group relative ${
              isPlayingAudio 
                ? 'bg-blue-100 animate-pulse' 
                : 'bg-gray-100 hover:bg-gray-200'
            }`}
            title="Play pronunciation (female, warm voice)"
          >
            <Volume2 className={`w-5 h-5 transition-colors ${
              isPlayingAudio ? 'text-blue-600' : 'text-gray-600'
            }`} />
            <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
              {isPlayingAudio ? 'Playing...' : 'Pronunciation'}
            </span>
          </button>
          <div className="flex items-center justify-center gap-1.5 text-[10px] sm:text-sm text-gray-400">
            <RotateCw className="w-3 h-3 sm:w-4 sm:h-4 animate-spin [animation-duration:3s]" />
            <span>{t('vocabulary.card.tapToFlip')}</span>
          </div>
        </div>
      </div>
    </>
  );

  const exampleGerman = card.exampleGerman || (card.examples && card.examples[0]?.german);
  const exampleEnglish = card.exampleEnglish || (card.examples && card.examples[0]?.english);

  const backContent = (
    <>
      <div className="text-center pb-1.5 sm:pb-3 border-b border-gray-200 flex-shrink-0">
        <h3 className="text-lg sm:text-2xl font-bold text-gray-800">{card.back}</h3>
      </div>
      {card.usage && (
        <div className={`flex items-center justify-center gap-2 py-2 px-3 ${redemittelStyle.bgColor} ${redemittelStyle.borderColor} border-x-0 border-t-0 border-b-2 flex-shrink-0`}>
          <Tag className={`w-4 h-4 ${redemittelStyle.textColor}`} />
          <h4 className="text-sm font-semibold text-gray-700">Verwendung:</h4>
        </div>
      )}
      <div className="space-y-1.5 sm:space-y-3 overflow-y-auto pt-1.5 sm:pt-3 flex-1 min-h-0">
        {card.usage && (
          <div className={`${redemittelStyle.bgColor} rounded-lg p-2 sm:p-3 border ${redemittelStyle.borderColor}`}>
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{card.usage}</p>
          </div>
        )}
        {exampleGerman && (
          <div
            className={`${redemittelStyle.bgColor} rounded-lg p-2 sm:p-3 border ${redemittelStyle.borderColor} cursor-pointer transition-all hover:shadow-md min-h-[44px] touch-manipulation`}
            onClick={toggleExample}
          >
            <p className="text-sm sm:text-base text-gray-800 leading-relaxed">
              🇩🇪 &quot;{exampleGerman}&quot;
            </p>
            {!revealedExample && (
              <p className="text-center text-[10px] sm:text-xs font-semibold text-gray-400 mt-0.5">
                {t('vocabulary.card.tapForTranslation')}
              </p>
            )}
            {revealedExample && exampleEnglish && (
              <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2 italic">
                🇬🇧 &quot;{exampleEnglish}&quot;
              </p>
            )}
          </div>
        )}
      </div>
    </>
  );

  const cardFaceClass = 'w-full h-full bg-white rounded-xl sm:rounded-2xl shadow-xl border-2 border-gray-200 flex flex-col overflow-hidden';
  const backFaceClass = 'w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl sm:rounded-2xl shadow-xl border-2 border-gray-200 p-2.5 sm:p-6 flex flex-col overflow-hidden';

  return (
    <div
      className="absolute top-0 left-0 w-full h-full"
      style={{ ...outerStyle, isolation: 'isolate' }}
    >
      <div
        onClick={isActive ? handleFlip : undefined}
        className="w-full h-full cursor-pointer touch-manipulation relative"
        style={isMobile ? {} : { perspective: '1000px' }}
      >
        {isMobile ? (
          <>
            <div
              className="w-full h-full absolute inset-0 bg-white"
              style={{
                opacity: isFlipped ? 0 : 1,
                pointerEvents: isFlipped ? 'none' : 'auto',
                transition: 'opacity 0.5s ease-in-out',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                textRendering: 'optimizeLegibility',
                zIndex: isFlipped ? 1 : 2
              }}
            >
              <div className={`${cardFaceClass} relative`}>{frontContent}</div>
            </div>
            <div
              className="w-full h-full absolute inset-0 bg-gray-50"
              style={{
                opacity: isFlipped ? 1 : 0,
                pointerEvents: isFlipped ? 'auto' : 'none',
                transition: 'opacity 0.5s ease-in-out',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                textRendering: 'optimizeLegibility',
                zIndex: isFlipped ? 2 : 1
              }}
            >
              <div className={backFaceClass}>{backContent}</div>
            </div>
          </>
        ) : (
          <div
            className="w-full h-full relative"
            style={{
              transformStyle: 'preserve-3d',
              transition: 'transform 0.5s ease-in-out',
              transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
            }}
          >
            <div
              className="absolute inset-0 w-full h-full bg-white"
              style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'rotateY(0deg)',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                textRendering: 'optimizeLegibility'
              }}
            >
              <div className={`${cardFaceClass} relative`}>{frontContent}</div>
            </div>
            <div
              className="absolute inset-0 w-full h-full bg-gray-50"
              style={{
                backfaceVisibility: 'hidden',
                WebkitBackfaceVisibility: 'hidden',
                transform: 'rotateY(-180deg)',
                WebkitFontSmoothing: 'antialiased',
                MozOsxFontSmoothing: 'grayscale',
                textRendering: 'optimizeLegibility'
              }}
            >
              <div className={backFaceClass}>{backContent}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RedemittelCard;
